import React, { useState, useEffect } from 'react';
import { Container } from 'reactstrap';
import Navbar from '../Navbar';
import Footer from '../Footer';
import GuestChatWidget from '../GuestChatWidget';
import '../landing.scss';
import SeoHead from '../../../../Components/Common/SeoHead';
import { MARKETS } from './feeRates';

const APP_URL = process.env.REACT_APP_APP_URL || '';

const FAQ_ITEMS = [
  {
    q: 'Is this tool really free?',
    a: 'Yes, you get 1 free AI optimization. Register for unlimited access to the title optimizer and the full Coderom dropshipping toolkit.',
  },
  {
    q: 'What is VeRO protection?',
    a: 'VeRO brands (Apple, Nike and many others) are trademark-protected on eBay. Listing them when you are not the brand owner risks removal. Our AI detects and removes or replaces these brands automatically.',
  },
  {
    q: 'Why 80 characters?',
    a: 'eBay limits listing titles to 80 characters. Our AI maximizes keyword coverage and search relevance within this limit, putting the most important terms first for the Cassini search algorithm.',
  },
  {
    q: 'Does it work for all eBay markets?',
    a: 'Yes — UK, US, Germany and Australia, each optimized for local spelling and search patterns so your titles match how buyers actually search in each marketplace.',
  },
  {
    q: 'What is Amazon to eBay dropshipping?',
    a: 'Dropshipping means listing Amazon products on eBay without holding stock — when an order comes in, it is fulfilled from Amazon. Coderom automates this end-to-end: sourcing, listing, repricing and stock sync.',
  },
];

interface Suggestion {
  title: string;
  score: number;
}

const TitleOptimizer: React.FC = () => {
  const [inputTitle, setInputTitle] = useState('');
  const [marketplace, setMarketplace] = useState('UK');
  const [category, setCategory] = useState('');
  const [brandFeatures, setBrandFeatures] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<{
    suggestions: Suggestion[];
    original_title: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hasUsed, setHasUsed] = useState(false);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

  // SSR-safe: localStorage only inside useEffect
  useEffect(() => {
    const used = localStorage.getItem('coderom_title_optimizer_used');
    if (used === 'true') setHasUsed(true);
  }, []);

  const charCount = inputTitle.length;
  const counterClass =
    charCount > 450 ? 'char-counter over' : charCount > 400 ? 'char-counter warn' : 'char-counter';

  const handleOptimize = async () => {
    if (inputTitle.trim().length < 5) return;
    setIsLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/public/tools/optimize-title', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: inputTitle.trim(),
          marketplace,
          category,
          features: brandFeatures,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || err.message || 'Optimization failed');
      }
      const data = await res.json();
      setResult(data);
      localStorage.setItem('coderom_title_optimizer_used', 'true');
      setHasUsed(true);
    } catch (e: any) {
      setError(e.message || 'Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopy = (text: string, index: number) => {
    navigator.clipboard.writeText(text);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const canSubmit = inputTitle.trim().length >= 5 && !isLoading;

  return (
    <div className="layout-wrapper landing">
      <SeoHead
        title="eBay Title Optimizer 2026 — AI-Powered | Coderom"
        description="Free AI-powered eBay title optimizer. Paste your Amazon title and get 4 SEO-optimized eBay titles instantly. VeRO-safe, 80-character optimized. Free tool by Coderom."
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": FAQ_ITEMS.map((f) => ({
            "@type": "Question",
            "name": f.q,
            "acceptedAnswer": { "@type": "Answer", "text": f.a },
          })),
        }) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.coderom.com/" },
            { "@type": "ListItem", "position": 2, "name": "eBay Title Optimizer", "item": "https://www.coderom.com/tools/title-optimizer" },
          ],
        }) }}
      />
      <Navbar />

      <div className="tools-page">
        {/* Hero */}
        <div className="calc-hero">
            <div className="calc-hero-bg" />
            <div className="calc-hero-content">
              <div className="hero-breadcrumb">
                <a href="/">Home</a>
                <span>›</span>
                <span>Tools</span>
                <span>›</span>
                <span>Title Optimizer</span>
              </div>
              <div className="hero-eyebrow">
                <i className="ri-magic-line" aria-hidden="true"></i>
                AI-Powered
              </div>
              <h1>eBay Title <span>Optimizer</span> 2026</h1>
              <p>Paste your Amazon title — AI rewrites it for eBay&apos;s Cassini search engine. VeRO-safe, 80-char optimized.</p>
              <div className="hero-badges">
                <span className="hero-badge">
                  <i className="ri-check-line" aria-hidden="true"></i>
                  AI-powered
                </span>
                <span className="hero-badge">
                  <i className="ri-check-line" aria-hidden="true"></i>
                  VeRO-safe
                </span>
                <span className="hero-badge">
                  <i className="ri-check-line" aria-hidden="true"></i>
                  80-char limit
                </span>
                <span className="hero-badge">
                  <i className="ri-check-line" aria-hidden="true"></i>
                  Free
                </span>
              </div>
            </div>
          </div>

        <div className="calc-wrapper">
          {/* Marketplace tabs */}
          <div className="market-tabs">
            {MARKETS.map((m) => (
              <button
                key={m.code}
                className={`market-tab${marketplace === m.code ? ' active' : ''}`}
                onClick={() => { setMarketplace(m.code); setResult(null); }}
              >
                <img
                  src={m.flagSrc}
                  alt={m.code}
                  style={{ width: 20, height: 15, borderRadius: 2, objectFit: 'cover' }}
                />
                <span>{m.code === 'UK' ? 'United Kingdom'
                  : m.code === 'US' ? 'United States'
                  : m.code === 'DE' ? 'Germany'
                  : 'Australia'}</span>
              </button>
            ))}
          </div>

          {/* Input card */}
          <div className="calc-card">
            <div className="section-label">Your product title</div>
            <textarea
              className="title-input"
              placeholder="Paste your Amazon product title here..."
              value={inputTitle}
              maxLength={500}
              onChange={(e) => { setInputTitle(e.target.value); setResult(null); }}
            />
            <div className={counterClass}>{charCount}/500</div>

            <div className="form-grid-2" style={{ marginTop: '1rem' }}>
              <div className="form-field">
                <label>Category <span style={{ color: '#94a3b8', fontWeight: 400 }}>(optional)</span></label>
                <div className="calc-input-wrap">
                  <input
                    type="text"
                    placeholder="e.g. Electronics"
                    value={category}
                    onChange={(e) => { setCategory(e.target.value); setResult(null); }}
                  />
                </div>
              </div>
              <div className="form-field">
                <label>Brand / features <span style={{ color: '#94a3b8', fontWeight: 400 }}>(optional)</span></label>
                <div className="calc-input-wrap">
                  <input
                    type="text"
                    placeholder="e.g. fast charging, 2m"
                    value={brandFeatures}
                    onChange={(e) => { setBrandFeatures(e.target.value); setResult(null); }}
                  />
                </div>
              </div>
            </div>

            {!hasUsed && (
              <button
                className="calc-btn"
                disabled={!canSubmit}
                onClick={handleOptimize}
              >
                {isLoading
                  ? (<><span className="btn-spinner" aria-hidden="true"></span>Optimizing...</>)
                  : 'Optimize with AI →'}
              </button>
            )}

            {error && <div className="error-msg">{error}</div>}

            {/* Results */}
            {result && result.suggestions && result.suggestions.length > 0 && (
              <div style={{ marginTop: '1.5rem' }}>
                <div className="section-label">Optimized titles</div>
                {result.suggestions.map((s, idx) => {
                  const isBest = idx === 0;
                  return (
                    <div className={`result-item${isBest ? ' best' : ''}`} key={idx}>
                      {isBest && <div className="best-badge">Best match</div>}
                      <div className="result-title">{s.title}</div>
                      <div className="result-meta">
                        <div className="score-wrap">
                          <span className="score-val">{s.score}/10</span>
                          <div className="score-bar-bg">
                            <div className="score-bar-fill" style={{ width: `${s.score * 10}%` }} />
                          </div>
                        </div>
                        <span className="char-pill">{s.title.length} chars</span>
                        <button
                          className={`copy-btn${copiedIndex === idx ? ' copied' : ''}`}
                          onClick={() => handleCopy(s.title, idx)}
                        >
                          <i className={copiedIndex === idx ? 'ri-check-line' : 'ri-file-copy-line'} aria-hidden="true"></i>
                          {copiedIndex === idx ? 'Copied' : 'Copy'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Gate card — shown after the 1 free use */}
            {hasUsed && (
              <div className="gate-card">
                <h3>Want unlimited AI optimization?</h3>
                <p>Start your 14-day free trial and optimize every listing, plus automated sourcing, repricing and stock sync.</p>
                <a href={`${APP_URL}/register`} className="gate-btn">
                  <i className="ri-rocket-line" aria-hidden="true"></i>Start your 14-day free trial
                </a>
                <div className="gate-sub">
                  Already have an account? <a href={`${APP_URL}/login`} style={{ color: '#f97316' }}>Sign in →</a>
                </div>
              </div>
            )}
          </div>

          {/* How It Works */}
          <section className="how-it-works">
            <Container>
              <h2>How the Title Optimizer Works</h2>
              <div className="steps-grid">
                <div className="step-card">
                  <div className="step-num">1</div>
                  <h3>Paste your Amazon title</h3>
                  <p>Copy the product title straight from Amazon and paste it in. Add an optional category or feature for sharper results.</p>
                </div>
                <div className="step-card">
                  <div className="step-num">2</div>
                  <h3>AI rewrites for eBay Cassini</h3>
                  <p>Our AI restructures keywords for eBay&apos;s search engine, strips VeRO brands and policy-risk words, and stays under 80 characters.</p>
                </div>
                <div className="step-card">
                  <div className="step-num">3</div>
                  <h3>Copy and list instantly</h3>
                  <p>Pick the best-scoring title, copy it with one click, and list your product with a search-optimized, compliant title.</p>
                </div>
              </div>
            </Container>
          </section>

          {/* FAQ */}
          <section className="tools-faq">
            <Container>
              <h2>eBay Title Optimizer — Frequently Asked Questions</h2>
              <div className="faq-list">
                {FAQ_ITEMS.map((item, idx) => (
                  <div className="faq-row" key={idx}>
                    <h3>{item.q}</h3>
                    <p>{item.a}</p>
                  </div>
                ))}
              </div>
            </Container>
          </section>

          {/* RELATED LINKS */}
          <section className="tools-related">
            <Container>
              <h2>Keep exploring</h2>
              <div className="related-links-grid">
                <a href="/blog/ebay-title-optimisation" className="related-link-card">
                  <span className="rl-label">Guide</span>
                  <span className="rl-title">How to Write eBay Titles That Rank</span>
                </a>
                <a href="/tools/ebay-fee-calculator" className="related-link-card">
                  <span className="rl-label">Free Tool</span>
                  <span className="rl-title">eBay Fee Calculator</span>
                </a>
                <a href="/tools/profit-calculator" className="related-link-card">
                  <span className="rl-label">Free Tool</span>
                  <span className="rl-title">Profit Calculator</span>
                </a>
                <a href="/compare/coderom-vs-yaballe" className="related-link-card">
                  <span className="rl-label">Compare</span>
                  <span className="rl-title">Coderom vs Yaballe</span>
                </a>
              </div>
            </Container>
          </section>

          {/* CTA */}
          <section className="tools-cta">
            <Container>
              <div className="cta-inner">
                <h2>Automate your eBay dropshipping</h2>
                <p>Coderom optimizes titles, reprices listings and syncs stock 24/7 — so every sale stays profitable.</p>
                <a href={`${APP_URL}/register`} className="cta-btn">Start Free Trial</a>
              </div>
            </Container>
          </section>
        </div>
      </div>

      <Footer />
      <GuestChatWidget />

      <style>{`
        .hero-breadcrumb { font-size: 12px; color: #94a3b8; margin-bottom: 12px; display: flex; gap: 6px; justify-content: center; flex-wrap: wrap; }
        .hero-breadcrumb a { color: #cbd5e1; text-decoration: none; }
        .hero-breadcrumb a:hover { color: #fb923c; }
        .tools-related { padding: 40px 0; }
        .tools-related h2 { font-size: 24px; font-weight: 700; color: #0f172a; margin-bottom: 18px; }
        .related-links-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 10px; }
        .related-link-card { display: flex; flex-direction: column; gap: 4px; padding: 14px 16px; background: #fff; border: 1px solid #e2e8f0; border-radius: 10px; text-decoration: none; transition: border-color 0.15s; }
        .related-link-card:hover { border-color: #f97316; }
        .rl-label { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em; color: #f97316; }
        .rl-title { font-size: 13px; font-weight: 500; color: #1e293b; line-height: 1.4; }
        .tools-page { min-height: 100vh; background: #f8fafc; padding: 0; }
        .calc-wrapper { max-width: 860px; margin: 0 auto; padding: 2rem 1rem; }
        .calc-hero {
          position: relative;
          overflow: hidden;
          border-radius: 0;
          margin-bottom: 2rem;
          padding: 80px 0 60px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .calc-hero-bg {
          position: absolute;
          inset: 0;
          background:
            linear-gradient(135deg,
              rgba(15,23,42,0.72) 0%,
              rgba(15,23,42,0.55) 100%
            ),
            url('/images/tools/hero-title-optimizer.jpg')
            center/cover no-repeat;
        }
        .calc-hero-content {
          position: relative;
          z-index: 1;
          padding: 2.5rem 2rem;
          max-width: 680px;
          width: 100%;
          text-align: center;
        }
        .hero-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 4px 14px;
          background: rgba(249,115,22,0.2);
          border: 1px solid rgba(249,115,22,0.4);
          border-radius: 999px;
          font-size: 12px;
          font-weight: 600;
          color: #fb923c;
          margin-bottom: 1rem;
          text-transform: uppercase;
          letter-spacing: 0.06em;
        }
        .calc-hero h1 {
          font-size: 2rem;
          font-weight: 700;
          color: #fff;
          margin-bottom: 0.5rem;
          line-height: 1.3;
        }
        .calc-hero h1 span { color: #fb923c; }
        .calc-hero p {
          font-size: 1rem;
          color: #cbd5e1;
          max-width: 520px;
          margin: 0 auto 1.5rem;
          line-height: 1.6;
        }
        .hero-badges {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
          justify-content: center;
        }
        .hero-badge {
          padding: 4px 12px;
          border-radius: 999px;
          background: rgba(255,255,255,0.1);
          border: 1px solid rgba(255,255,255,0.2);
          font-size: 13px;
          font-weight: 500;
          color: #e2e8f0;
          display: flex;
          align-items: center;
          gap: 4px;
        }
        .hero-badge i { font-size: 12px; color: #fb923c; }
        .market-tabs { display: flex; gap: 8px; margin-bottom: 1.5rem; flex-wrap: wrap; }
        .market-tab {
          padding: 8px 18px; border-radius: 999px; border: 1.5px solid #e2e8f0;
          font-size: 14px; font-weight: 500; cursor: pointer; color: #64748b;
          background: #fff; display: flex; align-items: center; gap: 6px; transition: all 0.15s;
        }
        .market-tab.active { border-color: #f97316; color: #ea580c; background: #fff7ed; }
        .calc-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 16px; padding: 1.75rem; }
        .section-label { font-size: 13px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; color: #94a3b8; margin-bottom: 0.75rem; }
        .form-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
        .form-field label { display: block; font-size: 13px; font-weight: 600; color: #475569; margin-bottom: 6px; }
        .calc-input-wrap { position: relative; display: flex; align-items: center; }
        .calc-input-wrap input {
          width: 100%; border: 1px solid #e2e8f0; border-radius: 8px;
          padding: 10px 12px; font-size: 14px; color: #1e293b; outline: none;
        }
        .calc-input-wrap input:focus { border-color: #f97316; box-shadow: 0 0 0 3px rgba(249,115,22,0.12); }
        .calc-btn {
          width: 100%; margin-top: 1.25rem; padding: 14px; border: none; border-radius: 10px;
          background: #f97316; color: #fff; font-size: 16px; font-weight: 700; cursor: pointer;
          display: flex; align-items: center; justify-content: center; gap: 8px; transition: all 0.2s;
        }
        .calc-btn:hover:not(:disabled) { background: #ea580c; }
        .calc-btn:disabled { opacity: 0.55; cursor: not-allowed; }
        .btn-spinner {
          width: 16px; height: 16px; border: 2px solid rgba(255,255,255,0.4);
          border-top-color: #fff; border-radius: 50%; animation: tospin 0.7s linear infinite;
        }
        @keyframes tospin { to { transform: rotate(360deg); } }

        .title-input {
          width: 100%; border: 1px solid #e2e8f0; border-radius: 8px; padding: 12px;
          font-size: 15px; resize: vertical; min-height: 100px; font-family: inherit;
          outline: none; color: #1e293b;
        }
        .title-input:focus { border-color: #f97316; box-shadow: 0 0 0 3px rgba(249,115,22,0.12); }
        .char-counter { font-size: 12px; color: #94a3b8; text-align: right; margin-top: 4px; }
        .char-counter.warn { color: #f97316; }
        .char-counter.over { color: #dc2626; }

        .result-item { border: 1px solid #e2e8f0; border-radius: 12px; padding: 1rem 1.25rem; margin-bottom: 10px; }
        .result-item.best { border-color: #f97316; background: #fff7ed; padding-top: 0; overflow: hidden; }
        .best-badge {
          display: inline-block; background: #f97316; color: #fff; font-size: 11px;
          font-weight: 600; padding: 2px 10px; border-radius: 0 0 8px 8px; margin-bottom: 8px;
        }
        .result-title { font-size: 15px; font-weight: 500; color: #1e293b; margin-bottom: 10px; line-height: 1.4; }
        .result-meta { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
        .score-wrap { display: flex; align-items: center; gap: 8px; flex: 1; min-width: 120px; }
        .score-val { font-size: 13px; font-weight: 700; color: #f97316; min-width: 32px; }
        .score-bar-bg { flex: 1; height: 6px; background: #f1f5f9; border-radius: 999px; overflow: hidden; }
        .score-bar-fill { height: 100%; background: #f97316; border-radius: 999px; }
        .char-pill { font-size: 11px; color: #64748b; background: #f8fafc; border: 1px solid #e2e8f0; padding: 2px 8px; border-radius: 999px; }
        .copy-btn {
          margin-left: auto; padding: 6px 14px; border-radius: 6px; border: 1px solid #e2e8f0;
          font-size: 12px; font-weight: 500; color: #64748b; background: #fff; cursor: pointer;
          display: flex; align-items: center; gap: 4px;
        }
        .copy-btn.copied { border-color: #16a34a; color: #16a34a; background: #f0fdf4; }

        .gate-card { background: #1e293b; border-radius: 16px; padding: 2rem; text-align: center; margin-top: 1rem; }
        .gate-card h3 { font-size: 18px; font-weight: 700; color: #fff; margin-bottom: 0.5rem; }
        .gate-card p { font-size: 14px; color: #94a3b8; margin-bottom: 1.25rem; }
        .gate-btn {
          display: inline-flex; align-items: center; gap: 8px; padding: 11px 28px;
          background: #f97316; color: #fff; border: none; border-radius: 10px;
          font-size: 14px; font-weight: 600; cursor: pointer; text-decoration: none;
        }
        .gate-btn:hover { background: #ea580c; color: #fff; }
        .gate-sub { font-size: 12px; color: #64748b; margin-top: 10px; }

        .error-msg { padding: 12px 16px; background: #fef2f2; border: 1px solid #fecaca; border-radius: 8px; font-size: 14px; color: #dc2626; margin-top: 1rem; }

        .how-it-works { padding: 64px 0 16px; }
        .how-it-works h2 { text-align: center; font-size: 26px; font-weight: 800; color: #1e293b; margin-bottom: 2rem; }
        .steps-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; }
        .step-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 14px; padding: 1.5rem; }
        .step-num {
          width: 36px; height: 36px; border-radius: 10px; background: #fff7ed; color: #ea580c;
          font-weight: 800; display: flex; align-items: center; justify-content: center; margin-bottom: 12px;
        }
        .step-card h3 { font-size: 16px; font-weight: 700; color: #1e293b; margin-bottom: 8px; }
        .step-card p { font-size: 14px; color: #64748b; line-height: 1.5; }

        .tools-faq { padding: 48px 0; }
        .tools-faq h2 { text-align: center; font-size: 26px; font-weight: 800; color: #1e293b; margin-bottom: 2rem; }
        .faq-list { max-width: 760px; margin: 0 auto; }
        .faq-row { border-bottom: 1px solid #e2e8f0; padding: 1.25rem 0; }
        .faq-row h3 { font-size: 16px; font-weight: 700; color: #1e293b; margin-bottom: 8px; }
        .faq-row p { font-size: 14px; color: #64748b; line-height: 1.6; margin: 0; }

        .tools-cta { padding: 80px 0; }
        .cta-inner {
          max-width: 720px; margin: 0 auto; text-align: center;
          background: linear-gradient(135deg, #f97316, #fb923c);
          border-radius: 20px; padding: 56px 32px; color: #fff;
        }
        .cta-inner h2 { font-size: 30px; font-weight: 800; margin-bottom: 12px; }
        .cta-inner p { font-size: 17px; opacity: 0.92; margin-bottom: 28px; }
        .cta-btn {
          display: inline-block; padding: 15px 36px; background: #fff; color: #ea580c;
          font-weight: 700; font-size: 16px; border-radius: 12px; text-decoration: none; transition: all 0.2s;
        }
        .cta-btn:hover { transform: translateY(-2px); box-shadow: 0 10px 28px rgba(0,0,0,0.2); color: #ea580c; }

        @media (max-width: 768px) {
          .form-grid-2 { grid-template-columns: 1fr; }
          .steps-grid { grid-template-columns: 1fr; }
        }
      `}</style>
    </div>
  );
};

export default TitleOptimizer;
